import {
  Alert,
  Button,
  Form,
  FormInstance,
  message,
  Segmented,
  Tabs,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { ReactElement, useState } from "react";
import { useParams } from "react-router-dom";
import { APPLICATION_TABS } from "../../../../constants/applicationTabs";
import { ApplicationTabNameType } from "../../../../enums/applicationTabName";
import { IValidationError } from "../../../../interfaces/error/IValidationError";
import { IApplicationTabsProps } from "../../../../interfaces/props/IApplicationTabsProps";
import {
  useChangeApplicationDataToBankMutation,
  useGetApplicationDataToBankQuery,
  useSendDataToSigningMutation,
  useSetApplicationBankStatusMutation,
} from "../../../../store/api/applicationBankApi/applicationBank.api";
import { SetApplicationStatusAction } from "../../../../store/api/applicationBankApi/config/config";
import { useSendDataToScoringMutation } from "../../../../store/api/applicationBankApi/scoring.api";
import { useAppSelector } from "../../../../store/reduxHooks";
import { SELECT_APPLICATIONS_DATA_TO_BANK_NO_DOCUMENTS } from "../../../../store/selectors/applicationDataToBankSelector";
import { RequiredFormsType } from "../../../../types/requiredForms";
import {
  UI_ITOTooltip,
  UIModuleStikeController,
} from "../../../../UI_ITO/indexUI";
import { Guard } from "../../../../utils/guards/Guard";
import { useConfirmModal } from "../../../../utils/Hooks/useConfirmModal";
import { plural } from "../../../../utils/utilities/plural";
import { validateObjectMember } from "../../../../utils/utilities/validateDataToBank";
import { ValidationError } from "../../../../utils/utilities/validationError";
import { AdditionalInformation } from "../AdditionalInformation/AdditionalInformation";
import { ApplicationDetailsDocList } from "../ApplicationDetailsDocList/ApplicationDetailsDocList";
import { Finance } from "../Finance/Finance";
import { Founders } from "../Founders/Founders";
import { Offer } from "../Offer/Offer";
import {
  kkb_document_signed,
  kkb_offer,
  kkb_paid,
  kkb_processing,
  kkb_refusal,
  kkb_revision,
} from "./config";
import { ConfirmModalChangeStatus } from "./ui/ConfirmModalChangeStatus/ConfirmModalChangeStatus";

import classes from "./ApplicationTabs.module.css";

/**
 * Компонент с вкладками подробной информации о заявке.
 * Отображает различные вкладки, такие как анкета, учредители, финансы и документы.
 * Позволяет пользователю взаимодействовать с данными заявки, отправлять их на подписание и скоринг,
 * а также изменять статус заявки.
 *
 * @param {IApplicationTabsProps} props - Свойства компонента, включая дочерние элементы.
 */
export function ApplicationTabs(props: IApplicationTabsProps): ReactElement {
  const { children = <></> } = props;
  const { applicationId, applicationBankId } = useParams();
  const [keyOfTabs, setKeyOfTabs] = useState("1");

  const [isFormsTouched, setIsFormsTouched] = useState(false);

  const { isLoading, data: applicationBankData } =
    useGetApplicationDataToBankQuery({
      applicationId: Number(applicationId),
      applicationBankId: Number(applicationBankId),
    });

  const {
    setConfirmModalMode,
    setIsConfirmModalOpen,
    isConfirmModalOpen,
    confirmModalMode,
    closeModal,
  } = useConfirmModal();

  const applicationField = {
    address_legal: applicationBankData?.contact_application.address_legal,
    post_address_organization:
      applicationBankData?.contact_application.post_address_organization,
    phone: applicationBankData?.contact_application.phone,
    email: applicationBankData?.contact_application.email,
    bik: applicationBankData?.bank_requisites.bik,
    name_bank: applicationBankData?.bank_requisites.name_bank,
    account_number: applicationBankData?.bank_requisites.account_number,
    account_correspondent:
      applicationBankData?.bank_requisites.account_correspondent,
  };
  const [sendDataForSaving] = useChangeApplicationDataToBankMutation({
    fixedCacheKey: `SHARED_SAVING_RESULT_${Number(applicationId)}`,
  });
  const [sendDataForSigning, { isLoading: isLoadingSendSigning }] =
    useSendDataToSigningMutation({
      fixedCacheKey: `SHARED_SIGNING_RESULT_${Number(applicationId)}`,
    });
  const [sendDataForScoring, { isLoading: isLoadingSendDataForScoring }] =
    useSendDataToScoringMutation();

  const [setApplicationBankStatus, { isLoading: isLoadingChangeStatus }] =
    useSetApplicationBankStatusMutation();

  const balanceData =
    applicationBankData?.balance_application.accountingBalance
      .balance_date_lc &&
    applicationBankData?.balance_application.accountingBalance.balance_date_lc;

  const hasNoDocuments = useAppSelector(
    SELECT_APPLICATIONS_DATA_TO_BANK_NO_DOCUMENTS,
  );

  const hasFounder =
    !!applicationBankData?.members_fl?.length &&
    applicationBankData?.members_fl.every((fl) => {
      return validateObjectMember(fl.member_passport_fl, [
        "passport_post_address",
        "passport_url",
        "passport_url_sign",
        "expired_date",
        "passport_document",
      ]);
    });

  function isFormEmpty() {
    return !validateObjectMember(applicationField, []);
  }

  const [questionnaireForm] = useForm();
  const [financeForm] = useForm();

  const applicatinTabs = [
    {
      key: "1",
      label: "Анкета",
      children: <AdditionalInformation form={questionnaireForm} />,
    },
    {
      key: "2",
      label: "Учредители",
      children: <Founders />,
    },
    {
      key: "3",
      label: "Финансы",
      children: <Finance form={financeForm} />,
      forceRender: true,
    },
    {
      key: "4",
      label: "Документы",
      children: <ApplicationDetailsDocList />,
    },
    {
      key: "5",
      label: "Предложение",
      children: <Offer />,
      forceRender: true,
    },
    {
      key: "6",
      label: "Чат",
      children: <Offer />,
      forceRender: true,
      disabled: true,
    },
    {
      key: "7",
      label: "История",
      children: <Offer />,
      forceRender: true,
      disable: true,
    },
  ];

  /**
   * Массив форм дополнительной заявки,
   * которые обязательно должны быть заполнены перед отправкой на подписание
   */
  const requiredForms: readonly FormInstance[] = [
    questionnaireForm,
    financeForm,
  ];

  if (!applicationId || !applicationBankId) return <></>;

  /**
   * @see {requiredForms}
   * @param {readonly FormInstance[]} requiredForms - массив обязательных к проверке форм
   * @returns {Promise<RequiredFormsType>} - промис с объектом данных из обязательных форм
   * @throws {ValidationError} - исключение с ошибками валидации
   */
  async function validateRequiredForms(
    requiredForms: readonly FormInstance[],
  ): Promise<RequiredFormsType> {
    // Общие результаты валидации всех обязательных форм - могут быть как rejected, так и fulfilled
    const validationResults = await Promise.allSettled(
      requiredForms.map((form) => form.validateFields()),
    );
    // Ошибки валидации
    const validationErrors = validationResults
      .filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      )
      .map((rejectedResult) => rejectedResult.reason);
    // Если есть ошибки валидации, бросаем исключение
    if (validationErrors.length > 0)
      throw new ValidationError("Произошла ошибка валидации", validationErrors);
    // Корректно заполненные формы
    const formValues = validationResults
      .filter(
        (result): result is PromiseFulfilledResult<RequiredFormsType> =>
          result.status === "fulfilled",
      )
      .map((fulfilledResult) => fulfilledResult.value);

    // Собираем значения в объект для отправки на backend
    return Object.assign({}, ...formValues);
  }

  /**
   * Находит названия вкладок, в которых находятся некорректно заполненные формы, по ключам форм
   * @param {IValidationError[]} validationErrors - массив ошибок валидации
   * @returns {ApplicationTabNameType[]} - массив названий вкладок с ошибками валидации
   */
  function getErrorTabsNames(
    validationErrors: IValidationError[],
  ): ApplicationTabNameType[] {
    const formsToTabs: Record<keyof RequiredFormsType, ApplicationTabNameType> =
      {
        contactApplication: ApplicationTabNameType.QUESTIONNAIRE,
        bankRequisites: ApplicationTabNameType.QUESTIONNAIRE,
        accountingBalance: ApplicationTabNameType.FINANCE,
      } as const;
    return validationErrors.map(
      (error) => formsToTabs[error.errorFields[0].name[0]],
    );
  }

  /**
   * Генерирует сообщение об ошибке с указанием на вкладки, в которых есть ошибки валидации
   * @param {ApplicationTabNameType[]} errorTabNames - Массив названий вкладок с ошибками валидации
   * @returns {string} - Сообщение об ошибке
   */
  function generateErrorMessage(
    errorTabNames: ApplicationTabNameType[],
  ): string {
    return `Пожалуйста, заполните обязательные поля формы во ${plural(
      errorTabNames.length,
      { one: "вкладке", few: "вкладках", many: "вкладках" },
    )} ${errorTabNames.map((name) => `"${name}"`).join(", ")}`;
  }

  /**
   * Валидирует обязательные формы и в случае успеха отправляет их на backend.
   * В случае ошибок валидации показывает message с указанием на вкладки с ошибками
   */
  async function handleSaveClick() {
    questionnaireForm.submit();
    try {
      const formValues = await validateRequiredForms(requiredForms);
      /** Для обработчика onFormFinish у Form Provider вызываем submit */
      requiredForms.forEach((form) => form.submit());

      sendDataForSaving({
        applicationId: Number(applicationId),
        applicationBankId: Number(applicationBankId),
        ...formValues,
      });
    } catch (error) {
      if (error instanceof ValidationError) {
        message.error(
          generateErrorMessage(
            getErrorTabsNames(error.cause as IValidationError[]),
          ),
        );
      }
    }
  }

  /**
   * Отправляет дополнительную заявку на подписание, статус заявки на backend меняется
   */
  async function handleSigningClick() {
    try {
      await sendDataForSigning({
        applicationBankId: Number(applicationBankId),
      }).unwrap();
      message.success("Заявка отправлена на подписание");
    } catch (serverError: any) {
      message.error("Не получилось отправить заявку на подписание");
      if (serverError?.data?.message) {
        message.error(serverError?.data.message);
      }
    }
  }

  /**
   * Отправляет дополнительную заявку на скоринг, данные скоринга появляются в Камкомбанке
   */
  async function handleScoringClick() {
    try {
      await sendDataForScoring({
        applicationBankId: Number(applicationBankId),
      }).unwrap();
      message.success("Заявка отправлена на скоринг");
    } catch (serverError) {
      message.error("Не получилось отправить заявку на скоринг");
    }
  }

  /**
   *  Меняет статус дополнительной формы(завки)
   */
  function handleChangeApplicationBankStatus(
    action: SetApplicationStatusAction,
  ) {
    setApplicationBankStatus({
      applicationBankId: Number(applicationBankId),
      applicationId: Number(applicationId),
      action,
    });
  }

  /**
   * Дизейблим кнопку "Отправить на подписание", если выполняется хотя бы одно из условий
   */
  const shouldSigningButtonBeDisabled =
    isLoading ||
    hasNoDocuments ||
    !hasFounder ||
    isFormsTouched ||
    isFormEmpty() ||
    isLoadingSendSigning ||
    !balanceData;

  /**
   * Если кнопка в состоянии disabled, показываем пользователю подсказку по hover на кнопку
   */
  function generateDisabledReasonMessage() {
    const messages = [];
    if (hasNoDocuments) messages.push("загрузить все документы");
    if (!hasFounder) messages.push("добавить учредителей");
    if (isFormsTouched) messages.push("сохранить внесённые изменения");

    return `Необходимо ${messages.join(", ")}`;
  }

  function handleOpenResigningButton() {
    setConfirmModalMode("resigning");
    setIsConfirmModalOpen(true);
  }

  function handleReturnFromRejectStatusButton() {
    setConfirmModalMode("return_from_reject");
    setIsConfirmModalOpen(true);
  }

  return (
    <>
      <div className={classes.applicationTabs__content}>
        {children}
        <Form.Provider
          onFormChange={() => {
            setIsFormsTouched(true);
          }}
          onFormFinish={() => {
            setIsFormsTouched(false);
          }}
        >
          {applicationBankData?.refusal_info && (
            <Alert
              className={classes.applicationTabsAlertError}
              type="error"
              showIcon
              message={`Причина отказа: ${applicationBankData.refusal_info.text}`}
            />
          )}
          <Segmented
            value={keyOfTabs}
            className={classes.applicationSegment}
            options={APPLICATION_TABS}
            onChange={(value) => setKeyOfTabs(value!)}
          />
          <Tabs
            defaultActiveKey="1"
            className={classes.applicationTabs}
            activeKey={keyOfTabs}
            items={applicatinTabs}
            type="card"
          />
        </Form.Provider>
      </div>

      <UIModuleStikeController>
        {/* Когда появляются кнопки */}
        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_paid]}
          >
            <Button
              htmlType="submit"
              onClick={() => handleChangeApplicationBankStatus("send-signing")}
              disabled={isLoading && isLoadingSendSigning}
            >
              Отправить на подписание клиенту
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_refusal]}
          >
            <Button
              className={classes.yellowBtn}
              onClick={handleReturnFromRejectStatusButton}
            >
              Вернуть на доработку
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_offer]}
          >
            <Button
              htmlType="submit"
              onClick={() => handleChangeApplicationBankStatus("refusal")}
              disabled={isLoading && isLoadingChangeStatus}
              danger
            >
              Отказать
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_offer]}
          >
            <Button
              htmlType="submit"
              onClick={() =>
                handleChangeApplicationBankStatus("agree-proposal")
              }
              disabled={isLoading && isLoadingChangeStatus}
            >
              Согласовать предложение
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_revision]}
          >
            <Button
              disabled={shouldSigningButtonBeDisabled}
              onClick={handleOpenResigningButton}
            >
              Отправить на повторное подписание
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_processing, kkb_revision]}
          >
            <Button
              htmlType="submit"
              onClick={handleSaveClick}
              disabled={isLoading}
            >
              Сохранить
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        {!!applicationBankData && (
          <Guard.ApplicationBankStatus
            status={applicationBankData?.status}
            allowedStatuses={[kkb_document_signed]}
          >
            <Button
              type="primary"
              onClick={handleScoringClick}
              disabled={shouldSigningButtonBeDisabled}
              loading={isLoadingSendDataForScoring}
              className={classes.buttonSendToScoring}
            >
              Отправить на скоринг
            </Button>
          </Guard.ApplicationBankStatus>
        )}

        <UI_ITOTooltip
          title={hasNoDocuments ? "Загрузите все документы" : ""}
        />

        <UI_ITOTooltip
          title={
            shouldSigningButtonBeDisabled ? generateDisabledReasonMessage() : ""
          }
        >
          {!!applicationBankData && (
            <Guard.ApplicationBankStatus
              status={applicationBankData?.status}
              allowedStatuses={[kkb_processing]}
            >
              <Button
                type="primary"
                htmlType="button"
                onClick={handleSigningClick}
                disabled={shouldSigningButtonBeDisabled}
              >
                Отправить на подписание
              </Button>
            </Guard.ApplicationBankStatus>
          )}
        </UI_ITOTooltip>
      </UIModuleStikeController>
      <ConfirmModalChangeStatus
        onCancel={closeModal}
        open={isConfirmModalOpen}
        mode={confirmModalMode}
        closeModal={closeModal}
      />
    </>
  );
}
